import axios from "axios";
import { warn as logWarn } from "../utils/logger";
import { CmsContent } from "../types/cms.types";

// Same runtime-first pattern as apiConfig.ts: env.js can override at deploy time
// The project id is public by design (visible in every browser request), so it ships
// as a default; the env vars stay as runtime/build-time overrides.
const DEFAULT_SANITY_PROJECT_ID = "ehbzz82x";

// Empty string counts as unset so the env.js placeholder doesn't shadow the fallbacks
const SANITY_PROJECT_ID =
    (window as any).REACT_APP_SANITY_PROJECT_ID ||
    process.env.REACT_APP_SANITY_PROJECT_ID ||
    DEFAULT_SANITY_PROJECT_ID;
const SANITY_DATASET =
    (window as any).REACT_APP_SANITY_DATASET ||
    process.env.REACT_APP_SANITY_DATASET ||
    "production";
const SANITY_API_VERSION = "v2026-07-01";

// Whether the CMS is configured; without a project id all content falls back to the built-in texts
export const cmsEnabled = SANITY_PROJECT_ID !== "";

// One request for everything the eshop needs from the CMS
const CMS_QUERY = `{
    "hero": *[_id == "heroSlides"][0].slides[]{
        _key, title, text, button, action, url, "imageUrl": image.asset->url, "avatarUrl": avatar.asset->url
    },
    "footerLinks": *[_id == "footerDocLinks"][0]{
        about{ label, url, "fileUrl": file.asset->url },
        policy{ label, url, "fileUrl": file.asset->url },
        allergens{ label, url, "fileUrl": file.asset->url },
        contacts{ label, url, "fileUrl": file.asset->url },
        gallery{ label, url, "fileUrl": file.asset->url },
        terms{ label, url, "fileUrl": file.asset->url },
        gdpr{ label, url, "fileUrl": file.asset->url },
        login{ label, url, "fileUrl": file.asset->url }
    },
    "aktuality": *[_type == "aktualita"] | order(date desc)[0...4]{
        _id, date, title, body, "imageUrl": image.asset->url
    },
    "partners": *[_type == "partner"] | order(order asc){
        _id, title, url, "logoUrl": logo.asset->url
    },
    "gallery": *[_id == "galleryImages"][0].images[]{ _key, "url": asset->url },
    "contactsPage": *[_id == "contactsPage"][0]{
        team[]{ _key, name, role, email, "imageUrl": image.asset->url },
        contactInfo{ phones, billingTitle, billingText, bankingTitle, bankingText }
    }
}`;

/**
* Fetches all CMS managed texts (hero banners, footer links, aktuality)
* from Sanity's public query API (published content only, CDN cached, no token).
* Returns null when the CMS is not configured or unreachable so callers keep their fallbacks.
*/
export const fetchCmsContent = async (): Promise<CmsContent | null> => {
    if (!cmsEnabled) return null;
    try {
        const url = `https://${SANITY_PROJECT_ID}.apicdn.sanity.io/${SANITY_API_VERSION}/data/query/${SANITY_DATASET}`;
        const response = await axios.get<{ result: CmsContent }>(url, {
            params: { query: CMS_QUERY },
        });
        return response.data.result ?? null;
    } catch (error) {
        logWarn("CMS content fetch failed, using built-in texts", error);
        return null;
    }
};
